import { TokenHeaderModel } from 'models/TokenHeaderModel'
import { isMalaysia } from 'utils/tenant'
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'
import { downloadImagefromDocUtil } from 'utils/api'
import store from 'redux/store'
import { appendSsmPdfPages } from 'pages/mys/ssmverification/ssmPdfMerge'

export const SIGNATURE_REQUIRED_ROLES = ['SALESUSER', 'CSE', 'ADMIN']

export const shouldAskForSignature = (tokenHeader?: TokenHeaderModel | null): boolean => {
    const roles = tokenHeader?.roles || []

    return isMalaysia(tokenHeader?.tenantId) && roles.some((role) => (
        SIGNATURE_REQUIRED_ROLES.includes(String(role).trim().toUpperCase())
    ))
}

export interface StampDocumentOptions {
    watermarkText?: string
    throwOnError?: boolean
}

export interface StampPdfParams extends StampDocumentOptions {
    pdfFile: File
    tokenHeader?: TokenHeaderModel | null
    signUrl?: string
}

export interface StampImageParams extends StampDocumentOptions {
    imageFile: File
    tokenHeader?: TokenHeaderModel | null
    signUrl?: string
}

const loadImage = (source: string): Promise<HTMLImageElement> => (
    new Promise((resolve, reject) => {
        const image = new Image()
        image.onload = () => resolve(image)
        image.onerror = reject
        image.src = source
    })
)

const canvasToBlob = (canvas: HTMLCanvasElement, type: string): Promise<Blob | null> => (
    new Promise((resolve) => canvas.toBlob(resolve, type, 0.92))
)

const getSignatureDataUrl = async (signUrl?: string): Promise<string> => {
    const effectiveSignUrl = signUrl || localStorage.getItem('user_sign_url') || ''
    if (!effectiveSignUrl) return ''

    try {
        const { data } = await downloadImagefromDocUtil(effectiveSignUrl)
        return typeof data === 'string' && data.startsWith('data:image/') ? data : ''
    } catch {
        return ''
    }
}

const getSalesIdentityNumber = (): string => {
    const state = store.getState()
    return state.auth?.salesUserIdentityNumber || localStorage.getItem('sales_user_identity_number') || ''
}

/**
 * Stamps every page of a PDF with a rectangular block containing:
 *   OSV on behalf of : Pine Payment Solutions
 *   Name of the sales person : <tokenHeader.ownername>
 *   Employee ID : <tokenHeader.employeeid>
 *   Employee of : PINE PAYMENT
 *   Signature: [signature image] (if available)
 * Returns the stamped file, or the original file if stamping fails.
 */
export async function stampPdf({ pdfFile, tokenHeader, signUrl, watermarkText, throwOnError }: StampPdfParams): Promise<File> {
    const effectiveSignUrl = signUrl || localStorage.getItem('user_sign_url') || ''
    const identityNumber = getSalesIdentityNumber()
    const designation = 'MERCHANT SALES'

    try {
        const pdfBytes = await pdfFile.arrayBuffer()
        let pdfDoc: PDFDocument
        try {
            pdfDoc = await PDFDocument.load(pdfBytes)
        } catch {
            // Match SSM upload behavior for PDFs that viewers can open but
            // pdf-lib cannot edit (including permission-encrypted statements).
            pdfDoc = await PDFDocument.create()
            await appendSsmPdfPages(pdfDoc, pdfFile, Number.MAX_SAFE_INTEGER)
        }
        const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
        const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
        const watermarkFont = watermarkText ? await pdfDoc.embedFont(StandardFonts.TimesRomanBold) : null
        const pages = pdfDoc.getPages()

        const salesPersonName = tokenHeader?.ownername || tokenHeader?.sub || '-'
        const employeeId = tokenHeader?.employeeid || '-'

        // Load signature image if available
        let signatureImage: any = null
        if (effectiveSignUrl) {
            try {
                const { data: base64Data } = await downloadImagefromDocUtil(effectiveSignUrl)
                if (base64Data && typeof base64Data === 'string') {
                    const parts = base64Data.split(',')
                    if (base64Data.startsWith('data:image/png')) {
                        signatureImage = await pdfDoc.embedPng(Uint8Array.from(atob(parts[1]), (c) => c.charCodeAt(0)))
                    } else if (base64Data.startsWith('data:image/jpeg')) {
                        signatureImage = await pdfDoc.embedJpg(Uint8Array.from(atob(parts[1]), (c) => c.charCodeAt(0)))
                    }
                }
            } catch { /* stamp without signature */ }
        }

        pages.forEach((page) => {
            const { width, height } = page.getSize()
            const visiblePage = page.getCropBox()
            const labelFontSize = 3.5
            const valueFontSize = 4
            const lineHeight = 6
            const darkGray = rgb(0.2, 0.2, 0.2)
            const gray = rgb(0.4, 0.4, 0.4)

            const rows: { label: string; value: string }[] = [
                { label: 'OSV on behalf of :', value: 'Pine Payment Solutions' },
                { label: 'Name of the sales person :', value: salesPersonName },
                { label: 'Employee ID :', value: employeeId },
                { label: 'Employee of :', value: 'PINE PAYMENT' },
                { label: 'NRIC / Passport No:', value: identityNumber },
                { label: 'Designation:', value: designation },
            ]

            const hasSignature = !!signatureImage
            const textHeight = rows.length * lineHeight
            const boxPadding = 3
            const boxWidth = 120
            const signatureHeight = 12
            const boxHeight = boxPadding * 2 + textHeight + (hasSignature ? lineHeight + lineHeight + 2 + signatureHeight : 0)
            const boxX = visiblePage.x + visiblePage.width - boxWidth - 10
            const boxY = visiblePage.y + boxHeight + 2
            const footerHeight = boxHeight + 4

            // Extend the page and move its original content up so the stamp
            // occupies its own footer instead of covering document content.
            page.setSize(width, height + footerHeight)
            page.translateContent(0, footerHeight)
            page.resetPosition()
            // Cropped/scanned PDFs can have a CropBox smaller than the MediaBox.
            // Extend that visible area too, otherwise the footer is clipped out.
            page.setCropBox(visiblePage.x, visiblePage.y, visiblePage.width, visiblePage.height + footerHeight)

            if (watermarkText && watermarkFont) {
                const size = Math.min(30, visiblePage.width / 36)
                const textWidth = watermarkFont.widthOfTextAtSize(watermarkText, size)
                page.drawText(watermarkText, {
                    x: visiblePage.x + Math.min(visiblePage.width * 0.6, visiblePage.width - textWidth - 10),
                    y: visiblePage.y + footerHeight + visiblePage.height * 0.55,
                    size,
                    font: watermarkFont,
                    color: rgb(0.6, 0.6, 0.6),
                })
            }

            page.drawRectangle({
                x: boxX,
                y: boxY - boxHeight,
                width: boxWidth,
                height: boxHeight,
                borderColor: gray,
                borderWidth: 0.5,
            })

            let textY = boxY - boxPadding - lineHeight
            rows.forEach((row) => {
                const labelWidth = font.widthOfTextAtSize(row.label, labelFontSize)
                page.drawText(row.label, { x: boxX + boxPadding, y: textY, size: labelFontSize, font, color: gray })
                page.drawText(row.value, { x: boxX + boxPadding + labelWidth + 2, y: textY, size: valueFontSize, font: boldFont, color: darkGray })
                textY -= lineHeight
            })

            if (signatureImage) {
                textY -= lineHeight + 2
                const sigLabelWidth = font.widthOfTextAtSize('Signature:', labelFontSize)
                page.drawText('Signature:', { x: boxX + boxPadding, y: textY, size: labelFontSize, font, color: gray })
                page.drawImage(signatureImage, { x: boxX + boxPadding + sigLabelWidth + 2, y: textY - 3, width: 35, height: signatureHeight })
            }
        })

        const stampedPdfBytes = await pdfDoc.save()
        return new File([stampedPdfBytes], pdfFile.name, { type: 'application/pdf' })
    } catch (error) {
        if (throwOnError) throw new Error(getDocumentStampErrorMessage(error))
        return pdfFile // fallback: return original
    }
}

/**
 * Adds the same OSV and salesperson stamp used for PDFs to a JPG or PNG image.
 * Returns the original image if it cannot be decoded or stamped.
 */
export async function stampImage({ imageFile, tokenHeader, signUrl, watermarkText }: StampImageParams): Promise<File> {
    const imageUrl = URL.createObjectURL(imageFile)
    const identityNumber = getSalesIdentityNumber()
    const designation = 'MERCHANT SALES'

    try {
        const image = await loadImage(imageUrl)
        const width = image.naturalWidth || image.width
        const height = image.naturalHeight || image.height
        if (!width || !height) return imageFile

        const scale = Math.max(0.5, Math.min(2.5, width / 1200))
        const padding = 6 * scale
        const margin = 10 * scale
        const lineHeight = 13 * scale
        const labelFontSize = 7 * scale
        const valueFontSize = 8 * scale
        const boxWidth = Math.min(260 * scale, width - (margin * 2))
        const signatureDataUrl = await getSignatureDataUrl(signUrl)
        let signatureImage: HTMLImageElement | null = null

        if (signatureDataUrl) {
            try {
                signatureImage = await loadImage(signatureDataUrl)
            } catch {
                signatureImage = null
            }
        }

        const rows = [
            { label: 'OSV on behalf of :', value: 'Pine Payment Solutions' },
            { label: 'Name of the sales person :', value: tokenHeader?.ownername || tokenHeader?.sub || '-' },
            { label: 'Employee ID :', value: tokenHeader?.employeeid || '-' },
            { label: 'Employee of :', value: 'PINE PAYMENT' },
            { label: 'NRIC / Passport No:', value: identityNumber },
            { label: 'Designation:', value: designation },
        ]
        const signatureHeight = signatureImage ? 21 * scale : 0
        const boxHeight = (padding * 2) + (rows.length * lineHeight) + (
            signatureImage ? lineHeight + signatureHeight : 0
        )
        const footerHeight = boxHeight + (margin * 2)
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height + footerHeight
        const context = canvas.getContext('2d')
        if (!context) return imageFile

        context.fillStyle = '#FFFFFF'
        context.fillRect(0, 0, canvas.width, canvas.height)
        context.drawImage(image, 0, 0, width, height)

        if (watermarkText) {
            context.fillStyle = '#999999'
            context.font = `bold ${width / 36}px "Times New Roman", serif`
            const textWidth = context.measureText(watermarkText).width
            context.fillText(watermarkText, Math.max(0, Math.min(width * 0.6, width - textWidth - margin)), height * 0.45)
        }

        const boxX = Math.max(margin, width - boxWidth - margin)
        const boxY = height + margin

        context.fillStyle = '#FFFFFF'
        context.strokeStyle = '#666666'
        context.lineWidth = Math.max(1, scale)
        context.fillRect(boxX, boxY, boxWidth, boxHeight)
        context.strokeRect(boxX, boxY, boxWidth, boxHeight)

        let textY = boxY + padding + valueFontSize
        rows.forEach(({ label, value }) => {
            context.fillStyle = '#666666'
            context.font = `${labelFontSize}px Arial, sans-serif`
            context.fillText(label, boxX + padding, textY)
            const labelWidth = context.measureText(label).width

            context.fillStyle = '#333333'
            context.font = `bold ${valueFontSize}px Arial, sans-serif`
            context.fillText(value, boxX + padding + labelWidth + (3 * scale), textY)
            textY += lineHeight
        })

        if (signatureImage) {
            textY += 2 * scale
            context.fillStyle = '#666666'
            context.font = `${labelFontSize}px Arial, sans-serif`
            context.fillText('Signature:', boxX + padding, textY + labelFontSize)
            const signatureLabelWidth = context.measureText('Signature:').width
            const signatureWidth = 60 * scale
            context.drawImage(
                signatureImage,
                boxX + padding + signatureLabelWidth + (3 * scale),
                textY,
                signatureWidth,
                signatureHeight,
            )
        }

        const outputType = imageFile.type.toLowerCase() === 'image/png'
            || imageFile.name.toLowerCase().endsWith('.png')
            ? 'image/png'
            : 'image/jpeg'
        const stampedBlob = await canvasToBlob(canvas, outputType)
        if (!stampedBlob) return imageFile

        return new File([stampedBlob], imageFile.name, {
            type: outputType,
            lastModified: imageFile.lastModified,
        })
    } catch {
        return imageFile
    } finally {
        URL.revokeObjectURL(imageUrl)
    }
}

export const getDocumentStampErrorMessage = (error: unknown): string => {
    const message = error instanceof Error ? `${error.name}: ${error.message}` : ''
    if (/encrypt|password/i.test(message)) {
        return 'This PDF is password-protected or encrypted. Please upload an unlocked, unencrypted copy so the verification stamp can be added.'
    }
    if (/WinAnsi|cannot encode/i.test(message)) {
        return 'The verification stamp contains characters the PDF font cannot display. Please check your salesperson name and identity details.'
    }
    if (/parse|invalid pdf|no pdf header|unexpected object|no pages/i.test(message)) {
        return 'This PDF could not be read. Please export it again as a PDF and retry the upload.'
    }
    return 'Unable to add the verification stamp. Please try another PDF or image.'
}

export async function stampDocument(
    file: File,
    tokenHeader?: TokenHeaderModel | null,
    signUrl?: string,
    options: StampDocumentOptions = {},
): Promise<File> {
    const fileType = file.type.toLowerCase()
    const fileName = file.name.toLowerCase()

    let result = file
    if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
        result = await stampPdf({ pdfFile: file, tokenHeader, signUrl, ...options })
    } else if (fileType.startsWith('image/') || /\.(jpe?g|png)$/.test(fileName)) {
        result = await stampImage({ imageFile: file, tokenHeader, signUrl, ...options })
    }
    if ((options.watermarkText || options.throwOnError) && result === file) {
        throw new Error('Unable to add the document watermark and verification stamp. Please try another PDF or image.')
    }
    return result
}
